import React, { createContext, useContext, useState, useEffect } from "react";
import { hr } from "./hr";
import { en } from "./en";
import { de } from "./de";

export type Translation = typeof hr;
type Language = "hr" | "en" | "de";

interface I18nContextType {
  t: Translation;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("hr");

  // Load language from localStorage or auto-detect via URL/IP/browser
  useEffect(() => {
    const savedLang = localStorage.getItem("language") as Language | null;
    // 1) URL override e.g. ?lang=de|en|hr
    try {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get("lang") as Language | null;
      if (urlLang && ["de","en","hr"].includes(urlLang)) {
        setLanguage(urlLang);
        localStorage.setItem("language", urlLang);
        return;
      }
    } catch {}

    // 2) Persisted choice
    if (savedLang) {
      setLanguage(savedLang);
      return;
    }

    const browserSuggest = (): Language => {
      const nav = (navigator?.language || navigator?.languages?.[0] || "en").toLowerCase();
      if (nav.includes("de")) return "de";
      if (nav.includes("hr") || nav.includes("sh")) return "hr";
      return "en";
    };

    const fallback = browserSuggest();
    // Try server hint first; fallback to browser on error/timeouts
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1800);
    fetch("/api/guess-language", { signal: controller.signal })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((data) => {
        const suggested = (data?.suggested as Language) || fallback;
        setLanguage(suggested);
        localStorage.setItem("language", suggested);
      })
      .catch(() => {
        setLanguage(fallback);
        localStorage.setItem("language", fallback);
      })
      .finally(() => clearTimeout(timeout));
  }, []);

  // Save language to localStorage
  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const translations: Record<Language, Translation> = {
    hr,
    en,
    de,
  };

  const value: I18nContextType = {
    t: translations[language] ?? hr,
    language,
    setLanguage: handleSetLanguage,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within I18nProvider");
  }
  return context;
}
